// Featured Collection Slider
// Problem Explanation:
// When dragging on a link and releasing, browsers fire a click event even after preventDefault in mousedown.
// This happens because browsers consider mousedown + mouseup on the same element as a click.
// Solution: Track drag state and prevent click events during/after drag using document-level listeners.

(function () {
  // Shared functions - defined once for all instances
  function preventLinkClick(e, state) {
    if (state.shouldPreventClick || state.hasMoved || state.isDragging) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  }

  function preventDocumentClick(e, state, track) {
    const target = e.target.closest("a");
    if (target && track.contains(target)) {
      if (state.shouldPreventClick || state.hasMoved || state.isDragging) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    }
  }

  function initFeaturedCollectionSlider(sectionId) {
    const section = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (!section) return;

    const track = section.querySelector("[data-slider-track]");
    const prevBtn = section.querySelector("[data-slider-prev]");
    const nextBtn = section.querySelector("[data-slider-next]");
    const progressCurrent = section.querySelector("[data-progress-current]");
    const progressFill = section.querySelector("[data-progress-fill]");
    const progressTotal = section.querySelector("[data-progress-total]");

    if (!track) return;

    // Instance-specific state
    const state = {
      currentView: 0,
      isDragging: false,
      startX: 0,
      startPosition: 0,
      currentPosition: 0,
      itemsPerView: 4,
      totalItems: track.children.length,
      totalViews: 0,
      viewportWidth: 0,
      scrollWidth: 0, // Total width of all content including gaps
      maxPosition: 0, // Maximum scroll position (allows 100% scroll)
      isTransitioning: false,
      dragThreshold: 50,
      clickThreshold: 8,
      clickedLink: null,
      hasMoved: false,
      shouldPreventClick: false,
    };

    state.totalViews = Math.ceil(state.totalItems / state.itemsPerView);

    // Instance-specific bound functions
    const boundPreventLinkClick = (e) => preventLinkClick(e, state);
    const boundPreventDocumentClick = (e) =>
      preventDocumentClick(e, state, track);

    function updateItemsPerView() {
      const isMobile = window.innerWidth <= 768;
      state.itemsPerView = isMobile ? 2 : 4;
      state.dragThreshold = isMobile ? 30 : 50;
      state.totalViews = Math.ceil(state.totalItems / state.itemsPerView);
      if (progressTotal) {
        progressTotal.textContent = String(state.totalViews).padStart(2, "0");
      }
      state.viewportWidth = track.offsetWidth;
      // Calculate actual scrollable width (includes all items + gaps)
      state.scrollWidth = track.scrollWidth;
      // Max position is the difference between total content width and viewport width
      state.maxPosition = -(state.scrollWidth - state.viewportWidth);
      goToView(state.currentView);
    }

    function goToView(viewIndex) {
      if (state.isTransitioning) return;

      state.currentView = Math.max(
        0,
        Math.min(viewIndex, state.totalViews - 1)
      );

      // Calculate position based on view index
      // For the last view, use maxPosition to ensure 100% scroll
      if (state.currentView === state.totalViews - 1) {
        state.currentPosition = state.maxPosition;
      } else {
        // For other views, calculate based on viewport width
        state.currentPosition = -(state.currentView * state.viewportWidth);
      }

      state.isTransitioning = true;
      track.style.transition = "transform 0.3s ease-out";
      track.style.transform = `translateX(${state.currentPosition}px)`;

      updateProgress();
      updateButtons();

      setTimeout(() => {
        state.isTransitioning = false;
      }, 300);
    }

    function updateProgress() {
      if (progressCurrent) {
        progressCurrent.textContent = String(state.currentView + 1).padStart(
          2,
          "0"
        );
      }
      if (progressFill) {
        const progress = ((state.currentView + 1) / state.totalViews) * 100;
        progressFill.style.width = `${progress}%`;
      }
    }

    function updateButtons() {
      if (prevBtn) {
        prevBtn.disabled = state.currentView === 0;
      }
      if (nextBtn) {
        nextBtn.disabled = state.currentView >= state.totalViews - 1;
      }
    }

    if (prevBtn) {
      prevBtn.addEventListener("click", () => {
        if (!state.isTransitioning && state.currentView > 0) {
          goToView(state.currentView - 1);
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", () => {
        if (
          !state.isTransitioning &&
          state.currentView < state.totalViews - 1
        ) {
          goToView(state.currentView + 1);
        }
      });
    }

    function startDrag(e) {
      if (e.type === "mousedown" && e.button !== 0) return;
      if (state.isTransitioning) {
        e.preventDefault();
        return;
      }

      // Don't start drag if touching a quick add button
      const quickAddBtn = e.target.closest(".quick-add-btn");
      if (quickAddBtn) {
        return; // Let the quick add button handle the event
      }

      state.clickedLink = e.target.closest("a");

      const allLinks = track.querySelectorAll("a");
      allLinks.forEach((link) => {
        link.style.pointerEvents = "none";
        link.addEventListener("click", boundPreventLinkClick, {
          capture: true,
        });
      });

      e.preventDefault();
      e.stopPropagation();

      state.isDragging = false;
      state.hasMoved = false;
      state.shouldPreventClick = false;
      state.startX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
      state.startPosition = state.currentPosition;

      document.addEventListener("mousemove", handleDrag);
      document.addEventListener("touchmove", handleDrag, { passive: false });
      document.addEventListener("mouseup", endDrag);
      document.addEventListener("touchend", endDrag);
      document.addEventListener("click", boundPreventDocumentClick, {
        capture: true,
      });
    }

    function handleDrag(e) {
      const currentX =
        e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
      const diff = state.startX - currentX;
      const absDiff = Math.abs(diff);

      state.hasMoved = true;

      if (!state.isDragging && absDiff < state.clickThreshold) return;

      if (!state.isDragging) {
        state.isDragging = true;
        track.style.transition = "none";
      }

      // Use maxPosition to allow 100% scrolling (accounts for gaps)
      state.currentPosition = Math.max(
        Math.min(state.startPosition - diff, 0),
        state.maxPosition
      );

      track.style.transform = `translateX(${state.currentPosition}px)`;
      e.preventDefault();
    }

    function removeClickPrevention() {
      const allLinks = track.querySelectorAll("a");
      allLinks.forEach((link) => {
        link.removeEventListener("click", boundPreventLinkClick, {
          capture: true,
        });
      });
    }

    function endDrag(e) {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("touchmove", handleDrag);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchend", endDrag);
      document.removeEventListener("click", boundPreventDocumentClick, {
        capture: true,
      });

      const endX =
        e.type === "touchend"
          ? e.changedTouches[0]
            ? e.changedTouches[0].clientX
            : state.startX
          : e.clientX;
      const pixelMovement = Math.abs(state.startX - endX);
      const positionChanged =
        Math.abs(state.currentPosition - state.startPosition) > 1;

      state.shouldPreventClick =
        state.isDragging ||
        state.hasMoved ||
        pixelMovement >= state.clickThreshold ||
        positionChanged;

      const allLinks = track.querySelectorAll("a");
      allLinks.forEach((link) => {
        link.style.pointerEvents = "";
        if (state.shouldPreventClick) {
          link.addEventListener("click", boundPreventLinkClick, {
            capture: true,
            once: true,
          });
        }
      });

      setTimeout(
        () => {
          state.shouldPreventClick = false;
          removeClickPrevention();
        },
        state.shouldPreventClick ? 300 : 100
      );

      if (
        state.isDragging ||
        state.hasMoved ||
        pixelMovement >= state.clickThreshold ||
        positionChanged
      ) {
        state.clickedLink = null;
      } else if (state.clickedLink && pixelMovement < state.clickThreshold) {
        const linkToFire = state.clickedLink;
        state.clickedLink = null;
        setTimeout(() => {
          if (linkToFire) {
            linkToFire.dispatchEvent(
              new MouseEvent("click", {
                bubbles: true,
                cancelable: true,
                view: window,
              })
            );
          }
        }, 10);
        return;
      }

      state.clickedLink = null;

      if (state.isDragging) {
        state.isDragging = false;
        const movement = state.currentPosition - state.startPosition;

        if (movement <= -state.dragThreshold) {
          goToView(Math.min(state.currentView + 1, state.totalViews - 1));
        } else if (movement >= state.dragThreshold) {
          goToView(Math.max(state.currentView - 1, 0));
        } else {
          goToView(state.currentView);
        }
      } else {
        goToView(state.currentView);
      }
    }

    track.addEventListener("mousedown", startDrag);
    track.addEventListener("touchstart", startDrag, { passive: false });

    track.querySelectorAll("img").forEach((img) => {
      img.setAttribute("draggable", "false");
      img.addEventListener("dragstart", (e) => e.preventDefault());
    });

    updateItemsPerView();
    window.addEventListener("resize", () => {
      updateItemsPerView();
    });

    updateProgress();
    updateButtons();
  }

  function initAllSliders() {
    const wrappers = document.querySelectorAll("[data-featured-slider]");
    wrappers.forEach((wrapper) => {
      const section = wrapper.closest("[data-section-id]");
      if (section) {
        const sectionId = section.getAttribute("data-section-id");
        if (sectionId) {
          initFeaturedCollectionSlider(sectionId);
        }
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAllSliders);
  } else {
    initAllSliders();
  }

  window.initFeaturedCollectionSlider = initFeaturedCollectionSlider;
})();
